<template>
    <!-- Not found / invalid ticket — show only the status, hide everything else -->
    <div v-if="!data.ok" class="bg-danger text-white rounded px-4 py-3 text-center">
        ❌ <span v-if="data.message" v-html="data.message" /><span v-else>{{ t('verify.notFound') }}</span>
    </div>

    <div v-else class="space-y-3 text-sm">
        <!-- Summary -->
        <table class="w-full bg-surface-card rounded overflow-hidden">
            <tbody>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.found') }}</td>
                    <td class="p-2"><span>✅</span> <span v-if="data.message" v-html="data.message" /></td>
                </tr>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.email') }}</td>
                    <td class="p-2">{{ data.email }}</td>
                </tr>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.entries') }}: {{ data.entries }}</td>
                    <td class="p-2">{{ t('verify.people') }}: {{ data.people }}</td>
                </tr>
                <tr v-if="(data.entries ?? 0) > 1" class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.entryPause') }}</td>
                    <td class="p-2">{{ data.entryPause }}</td>
                </tr>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.purchased') }}</td>
                    <td class="p-2">{{ formatDate(data.created, 'D.M.YYYY HH:mm') }}</td>
                </tr>
                <tr v-if="data.validUntil">
                    <td class="p-2 opacity-70">{{ t('verify.validUntil') }}</td>
                    <td class="p-2">{{ formatDate(data.validUntil, 'D.M.YYYY HH:mm') }}</td>
                </tr>
            </tbody>
        </table>

        <!-- Event / ticketset / price -->
        <table class="w-full bg-surface-card rounded overflow-hidden">
            <tbody>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.event') }}</td>
                    <td class="p-2">
                        {{ data.eventName }}
                        <small class="opacity-60">({{ data.eventId }} / {{ data.eventBzucoId }})</small>
                    </td>
                </tr>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.ticketset') }}</td>
                    <td class="p-2">
                        {{ data.ticketsetName }}
                        <small class="opacity-60">({{ data.ticketsetId }} / {{ data.ticketsetBzucoId }})</small>
                    </td>
                </tr>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.price') }}</td>
                    <td class="p-2">
                        {{ data.priceName }}
                        <small class="opacity-60">({{ data.priceId }} / {{ data.priceBzucoId }})</small>
                    </td>
                </tr>
                <tr class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.zones') }}</td>
                    <td class="p-2">
                        <template v-if="zones.length">
                            <div v-for="zone in zones" :key="zone.id ?? zone.name ?? ''">
                                {{ zone.name }} <small class="opacity-60">({{ zone.id }} / {{ zone.bzucoId }})</small>
                            </div>
                        </template>
                        <template v-else>{{ t('verify.noZones') }}</template>
                    </td>
                </tr>
                <tr>
                    <td class="p-2 opacity-70">{{ t('verify.slots') }}</td>
                    <td class="p-2">
                        <template v-if="slots.length">
                            <div v-for="slot in slots" :key="slot.id ?? slot.startAt ?? ''">
                                {{ formatDate(slot.startAt, 'D.M.YYYY HH:mm') }}
                                <small class="opacity-60">({{ slot.id }} / {{ slot.bzucoId }})</small>
                            </div>
                        </template>
                        <template v-else>{{ t('verify.noSlots') }}</template>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Usage per zone -->
        <table v-if="usageForZone.length" class="w-full bg-surface-card rounded overflow-hidden">
            <tbody>
                <tr v-for="row in usageForZone" :key="row.id ?? row.name ?? ''" class="border-b border-white/5">
                    <td class="p-2 opacity-70">{{ t('verify.usageForZone') }}<br />{{ row.name }}</td>
                    <td class="p-2">
                        <template v-if="row.remains === null || row.remains === undefined">
                            <span v-if="!row.used">✅</span><span v-else>❌</span>
                        </template>
                        <template v-else>
                            {{ t('verify.remains') }}:
                            <span v-if="row.remains">✅ {{ row.remains }}</span>
                            <span v-else>❌ &mdash;</span>
                            <div v-if="row.display" class="opacity-80 mt-1" v-html="row.display" />
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Usage logs -->
        <table v-if="usageLogs.length" class="w-full bg-surface-card rounded overflow-hidden">
            <thead>
                <tr class="border-b border-white/10">
                    <th colspan="3" class="p-2 text-left font-semibold">{{ t('verify.usageLogs') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, i) in usageLogs" :key="i" class="border-b border-white/5">
                    <td class="p-2">{{ formatDate(row.created, 'D.M.YYYY HH:mm') }}</td>
                    <td class="p-2">
                        {{ row.deviceName }} <small class="opacity-60">({{ row.deviceId }})</small>
                    </td>
                    <td class="p-2">
                        {{ row.zoneName }} <small class="opacity-60">({{ row.zoneId }})</small>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Debug: query + raw data hidden behind a small "Q" toggle (always available) -->
    <div class="pt-2">
        <button
            class="px-2 py-1 text-xs rounded bg-white/10 hover:bg-white/20 font-mono"
            @click="showDebug = !showDebug"
        >
            {{ t('verify.showDebug') }}
        </button>
        <div v-if="showDebug" class="mt-2 space-y-2">
            <div>
                <div class="text-xs opacity-60">{{ t('verify.debugData') }}</div>
                <pre class="text-[10px] bg-black/40 rounded p-2 overflow-x-auto">{{ prettyData }}</pre>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { formatDate } from '@/services/util/date'
    import type { InfoData } from '@/stores/verify'

    const props = defineProps<{ data: InfoData }>()
    const { t } = useI18n()

    const showDebug = ref(false)

    const zones = computed(() => (props.data.zones ?? []).filter((z): z is NonNullable<typeof z> => z !== null))
    const slots = computed(() => (props.data.slots ?? []).filter((s): s is NonNullable<typeof s> => s !== null))
    const usageForZone = computed(() =>
        (props.data.usageForZone ?? []).filter((u): u is NonNullable<typeof u> => u !== null)
    )
    const usageLogs = computed(() => (props.data.usageLogs ?? []).filter((l): l is NonNullable<typeof l> => l !== null))

    const prettyData = computed(() => JSON.stringify(props.data, null, 2))
</script>
